import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ObservableQueryFields } from '@apollo/client';

import { ButtonModel, Checkbox } from '@notino/react-styleguide';
import {
  GetReviewsQuery,
  GetReviewsQueryVariables,
} from '@notino/shared/definitions/types';

import { useShouldShowReviewPage } from '@containers/ProductDetailContainer/ProductDetail/hooks/useShouldShowReviewPage';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import messages from '../../../../../messages';
import {
  useReviewSortingActions,
  useReviewSortingStore,
} from '../SortFilterBar/Stores/sortingStore';
import {
  ShowNextWrapper,
  StyledButton,
  StyledCol,
  ReviewsList,
  ShowAllReviewsLink,
} from '../styled';

import { SingleReviewData } from './SingleReviewData';
import { CheckboxWrapper } from './styled';

interface IFetchDoneProps {
  productReviewCount: number;
  reviewsData: NonNullable<GetReviewsQuery['reviewsAndFilters']>['data'];
  productCode: string;
  isAllReviewsPage?: boolean;
  loadReviews: ObservableQueryFields<
    GetReviewsQuery,
    GetReviewsQueryVariables
  >['fetchMore'];
}

export const FetchDoneBlock: React.FC<IFetchDoneProps> = ({
  productReviewCount,
  reviewsData,
  loadReviews,
  isAllReviewsPage,
}) => {
  const { getTimeFromInit } = useTrackingContext();
  const [isLoading, setLoading] = React.useState(false);

  const { incNextPage, toggleHideTranslatedReviews } =
    useReviewSortingActions();

  const {
    orderBy,
    orderDesc,
    nextPage,
    hideTranslated,
    allowHideTranslatedCheckbox,
  } = useReviewSortingStore();

  const [loadMoreError, setLoadMoreError] = React.useState<boolean>(false);
  const { product } = useProductDetailContext();
  const { formatMessage } = useIntl();

  const ratingSummary = product.reviewOverview?.rating ?? 0;

  const shouldShowReviewPage = useShouldShowReviewPage(
    productReviewCount,
    ratingSummary
  );

  const allReviewsPageLink = formatMessage(messages.redirectReviewsPageLink);

  const handleLoadMore = React.useCallback(async () => {
    try {
      dispatchTrackingEvent({
        event: 'element_click',
        element: {
          action: 'show_more_reviews',
          type: isAllReviewsPage ? 'review' : 'product_detail',
          timing: getTimeFromInit(),
          interaction: 'click',
          name: 'reviews',
        },
        _clear: true,
      });

      setLoading(true);

      await loadReviews({
        variables: {
          masterProductId: product.catalogMasterId,
          orderBy,
          orderDesc,
          page: nextPage,
          hideTranslated,
        },
      });
      incNextPage();
    } catch (e) {
      setLoadMoreError(true);
    } finally {
      setLoading(false);
    }
  }, [
    isAllReviewsPage,
    getTimeFromInit,
    loadReviews,
    product.catalogMasterId,
    orderBy,
    orderDesc,
    nextPage,
    hideTranslated,
    incNextPage,
  ]);

  if (!reviewsData) {
    return null;
  }

  const displayShowMoreButton =
    productReviewCount > reviewsData.reviews.length &&
    reviewsData.totalPages !== reviewsData.page &&
    reviewsData.reviews.length > 0;

  const shouldShowAllReviewsLink = !!shouldShowReviewPage && !isAllReviewsPage;

  const handleTrackShowAllReviews = () => {
    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        action: 'all',
        type: 'product_detail',
        timing: getTimeFromInit(),
        name: 'all_reviews',
      },
      _clear: true,
    });
  };

  return (
    <>
      <StyledCol data-cypress="reviews-listing" xs={12}>
        {allowHideTranslatedCheckbox && (
          <CheckboxWrapper>
            <Checkbox
              checked={hideTranslated}
              onChange={toggleHideTranslatedReviews}
            >
              <FormattedMessage {...messages.onlyOriginalReviews} />
            </Checkbox>
          </CheckboxWrapper>
        )}

        <ReviewsList>
          {reviewsData.reviews?.map((item, index) => (
            <SingleReviewData item={item} key={item.id} index={index} />
          ))}
        </ReviewsList>
      </StyledCol>

      {(shouldShowAllReviewsLink || displayShowMoreButton) && (
        <StyledCol xs={12}>
          <ShowNextWrapper shouldShowAllReviewsLink={shouldShowAllReviewsLink}>
            {displayShowMoreButton && (
              <StyledButton
                data-cypress="reviews-loadMore"
                data-testid="reviews-loadMore"
                isLoading={isLoading}
                error={Boolean(loadMoreError)}
                buttonStyle={ButtonModel.Styles.secondary}
                onClick={handleLoadMore}
              >
                <FormattedMessage
                  {...(loadMoreError
                    ? messages.sendReviewRetry
                    : messages.showNext)}
                />
              </StyledButton>
            )}

            {shouldShowAllReviewsLink && (
              <ShowAllReviewsLink
                onClick={handleTrackShowAllReviews}
                href={`${allReviewsPageLink ?? ''}${product.url ?? ''}`}
              >
                <FormattedMessage {...messages.reviewsShowAll} />
              </ShowAllReviewsLink>
            )}
          </ShowNextWrapper>
        </StyledCol>
      )}
    </>
  );
};
